<script lang="ts">
	import { devices_status, devices_settings } from 'src/stores';
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';

	import ControlOnOffZ from 'src/components/Controls/Single/ControlOnOffZ.svelte';
	import ControlDirectionZ from 'src/components/Controls/Single/ControlDirectionZ.svelte';
	import InputsReaderVisualizer from 'src/components/Controls/Indicators/InputsReaderVisualizer.svelte';
	import StatusBar from 'src/components/Lists/Devices/StatusBar.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';

	import { AdvancedCardV2, LabelListV2, Text } from '@Shelly/shelly-design-system';

	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { deviceHasZWaveInputs, isZWaveActuatorDevice } from 'src/helpers/z_wave_status_helpers';
	import { getZWaveDeviceDisplayName } from 'src/helpers/z_wave_gateway_helpers';
	import { get_mode, load_image_from, real_id } from 'src/helpers/interface_helpers';
	import {
		mapIconSolidWeightToLight,
		mapLegacyColorToToken,
		mapOldCustomIconFromDBtoNew,
	} from 'src/helpers/new-layout-helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let device: Device;

	$: status = $devices_status?.[real_id(device.id)];
	$: settings = $devices_settings?.[real_id(device.id)];

	$: title = getZWaveDeviceDisplayName(device, status, settings);
	$: statuses = status_configurator(status, device, settings);
	$: labelList = getCustomizeLabelList(device, statuses).map((label) => ({
		...label,
		icon: mapIconSolidWeightToLight(label?.icon),
		backgroundColor: mapLegacyColorToToken(label?.type),
	}));
	$: isOnline = Boolean(status?.online);
	$: isCover =
		device?.type === 'ZW-COVER' ||
		device?.category === 'roller' ||
		get_mode(device, status, settings) === 'roller';
	$: isWaveActuator = isZWaveActuatorDevice(device, status);
	$: isWaveInputsReader =
		device.type === 'ZW-INPUT' ||
		device.category === 'inputs_reader' ||
		(deviceHasZWaveInputs(status) && !isWaveActuator);

	$: typeImage =
		typeof device?.type === 'string' && device.type.startsWith('ZW-')
			? `images/device_images/${device.type}.png`
			: undefined;
	$: avatarImage = device?.image
		? load_image_from(device.image, device?.localImage ? '' : device?.server).replace('thumb_', '')
		: typeImage;
	$: avatar = {
		image: avatarImage,
		backgroundColor:
			!!device.backgroundColor && device.backgroundColor !== ''
				? device.backgroundColor
				: 'color-base',
		icon:
			mapOldCustomIconFromDBtoNew(device.icon) ||
			(!avatarImage ? 'fak fa-solid-shelly' : undefined),
	};

	const onCardClick = () => {
		setSidePanelState({ type: 'device', id: device.id, open: true });
	};
</script>

<AdvancedCardV2
	{avatar}
	{title}
	hideSlotMain={false}
	hideSlotRight={false}
	hideSlotRightBadge
	hideSlotEditModeAction
	isFocusable
	on:click={onCardClick}
>
	<svelte:fragment slot="statusBar">
		<StatusBar {device} {status} {settings} />
	</svelte:fragment>

	<svelte:fragment slot="main">
		{#if !isOnline}
			<Text styleType="body-small" class="text-muted">
				{capFirstChar(
					$format('device_status_no_connection', {
						default: 'Device is offline',
					}),
				)}
			</Text>
		{:else if labelList.length > 0}
			<LabelListV2
				a11yProps={{ isInteractive: true }}
				{labelList}
				size="m"
				on:label-click={onCardClick}
			/>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="right">
		{#if isOnline}
			{#if isCover}
				<ControlDirectionZ
					useV2Components
					devBtnsV2Props={{
						deviceButtonV2: { size: 'm' },
						doubleDeviceButtonV2: { size: 'm' },
						tripleDeviceButtonV2: { size: 'm' },
					}}
					{device}
					{status}
					class="align-self-center"
				/>
			{:else if isWaveInputsReader}
				<InputsReaderVisualizer
					useV2Components
					showLabel
					{device}
					{status}
					{settings}
					deviceButtonV2Props={{ size: 'm' }}
					isInCard
					class="align-self-center"
				/>
			{:else if isWaveActuator}
				<ControlOnOffZ
					useV2Components
					{device}
					{status}
					{settings}
					deviceButtonV2Props={{ size: 'm' }}
					class="align-self-center"
				/>
			{/if}
		{/if}
	</svelte:fragment>
</AdvancedCardV2>
